import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { tabPress } from "../../../shared/inApp";
import { usePhone } from "../../../shared/phone/media";
import { IN_APP, onFromApp, postToApp } from "../../lib/inApp";
import { Icon, type IconName } from "../ui/Icon";
import type { Badges } from "./badges";
import { isTabRoot, TAB_PLACES, type TabId } from "./tabs";

const LOOK: Record<TabId, { label: string; icon: IconName; badge?: keyof Badges }> = {
  home: { label: "Home", icon: "home" },
  needs: { label: "Needs you", icon: "inbox", badge: "needs" },
  products: { label: "Products", icon: "box" },
  library: { label: "Library", icon: "book", badge: "lessons" },
  more: { label: "More", icon: "more" },
};

/**
 * The tabs along the bottom of a phone: the four daily places and More for the rest. Shown only on a phone,
 * and only on a tab's own screen. Inside the Flashsuite phone app every tab has its own kept screen, so a press
 * on another tab asks the app for it instead of moving this one, and lights that tab at once while the app opens
 * it (flashsuite-phone docs/APP-MODE.md sections 3 and 10).
 */
export function TabBar({ badges }: { badges: Badges }) {
  const { pathname } = useLocation();
  const phone = usePhone();
  // The tab pressed inside the app, lit until the app hides or shows this screen, or the page moves.
  const [asked, setAsked] = useState<{ id: TabId; at: string } | null>(null);
  useEffect(() => {
    if (!IN_APP) return;
    return onFromApp((m) => {
      if (m.kind === "hidden" || m.kind === "shown") setAsked(null);
    });
  }, []);
  if (!phone || !isTabRoot(pathname)) return null;
  const lit = asked?.at === pathname ? asked.id : null;

  return (
    <nav className="pn-tabs" aria-label="Main">
      {TAB_PLACES.map((tab) => {
        const { label, icon, badge } = LOOK[tab.id];
        const n = badge ? badges[badge] : 0;
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) => ((lit ? tab.id === lit : isActive) ? "pn-tab on" : "pn-tab")}
            aria-label={n ? `${label}, ${n} waiting` : label}
            onClick={(e) => {
              const ask = tabPress(IN_APP, TAB_PLACES, pathname, tab);
              if (!ask) return;
              e.preventDefault();
              postToApp(ask);
              setAsked({ id: tab.id, at: pathname });
            }}
          >
            <Icon name={icon} />
            <span>{label}</span>
            {n ? (
              <b className="pn-badge" aria-hidden="true">
                {n > 99 ? "99+" : n}
              </b>
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}
